import { FatTransactionsPageAllocationTransactionsQuery } from '@/__generated__/graphql';
import Input from '@/components/basicComponents/input';
import { formatDollarInputValue, formatMaskedDollarInputValue } from '@/components/basicComponents/input/utils';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import { PROPOSAL_TYPE } from '@/types/investments';
import { useMutation } from '@apollo/client';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { UPDATE_ALLOCATION_AMOUNT_MUTATION } from '../queries';
import { AllocationWithUpdated } from '../table/table';

interface EditAmountProps {
  isOpen: boolean;
  allocation: AllocationWithUpdated | null;
  onClose: () => void;
  updateLocalAllocation: (updatedAllocation: FatTransactionsPageAllocationTransactionsQuery['allocationTransactions']['allocations'][number]) => void;
  startFlashRowAnimation: (allocationId: string) => void;
}

const EditAmount = ({ isOpen, allocation, onClose, updateLocalAllocation, startFlashRowAnimation }: EditAmountProps) => {
  const { handleSubmit, setValue, control } = useForm<{ amount: string }>({ defaultValues: { amount: '' } });

  const [updateAllocationAmount] = useMutation(UPDATE_ALLOCATION_AMOUNT_MUTATION, {
    fetchPolicy: 'no-cache'
  });

  useEffect(() => {
    if (!allocation) return;

    setValue('amount', allocation?.committedCapital?.toString() ?? '');
  }, [allocation]);

  const saveValue = (data: { amount: string }) => {
    updateAllocationAmount({
      variables: {
        data: {
          id: allocation.id,
          amount: parseFloat(data.amount)
        }
      },
      onCompleted: (data) => {
        updateLocalAllocation(data.updateAllocationAmount);
      },
      onError: (error) => {
        console.error(error);
      }
    });
    startFlashRowAnimation(allocation.id);
    onClose();
  };

  if (!allocation) return <></>;

  return (
    <ModalWrappedContent
      isOpen={isOpen}
      onClose={onClose}
      title="Edit Investment Amount"
      confirmButton={{
        name: 'Save',
        onClick: handleSubmit(saveValue)
      }}
    >
      <Controller
        name="amount"
        control={control}
        rules={{
          required: {
            value: true,
            message: 'Amount is required'
          },
          min: {
            value: 1,
            message: 'Amount must be greater than 0'
          },
          validate: (value) => {
            const amount = parseFloat(value);
            if (allocation.proposalType === PROPOSAL_TYPE.INITIAL && amount < allocation.investment.minimum) {
              return 'Cannot be less than fund minimum';
            }
            if (allocation.proposalType === PROPOSAL_TYPE.INCREASE && amount <= 0) {
              return 'Cannot be less than 0';
            }
            return true;
          }
        }}
        render={({ field: { onChange, value }, fieldState: { error } }) => (
          <>
            <Input
              value={value ? formatMaskedDollarInputValue(value) : ''}
              onChange={(e) => {
                const formattedValue = formatDollarInputValue(e.target.value);
                onChange(formattedValue);
              }}
              label={allocation.proposalType === 'Increase' ? 'Follow-on Investment Amount' : 'Initial Investment Amount'}
              errorText={error?.message}
              styleType={value ? 'success' : 'default'}
              isRequiredField
              withDollarSign
              autoFocus
              description={allocation.proposalType === 'Increase' ? '' : `Initial Investment minimum: $${allocation.investment.minimum}`}
            />
          </>
        )}
      />
    </ModalWrappedContent>
  );
};

export default EditAmount;
